import { defineStore } from "pinia";
import UserService from "@/services/userService";
import { toast } from "@/utils/toast";
import { createScopedCache } from "./_cache";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

const userListCache = createScopedCache("users:list");
const userDetailCache = createScopedCache("users:detail");

const USERS_LIST_TTL = 2 * 60 * 1000;
const USERS_DETAIL_TTL = 5 * 60 * 1000;

export const useUserStore = defineStore("user", {
  state: () => ({
    users: [],
    meta: { total: 0, per_page: 25, current_page: 1 },
    loading: false,
    error: null,
    currentParams: {},
    byId: {},
  }),
  actions: {
    async list(params = {}, options = {}) {
      return await this.fetchUsers(params, options);
    },

    async fetchUsers(params = {}, options = {}) {
      if (!(await guardPlanModule(MODULE_KEYS.ADMIN_SETTINGS))) {
        this.users = [];
        this.meta = { total: 0, per_page: 25, current_page: 1 };
        return { data: { data: [], meta: this.meta } };
      }

      const mergedParams = { ...params };
      this.loading = true;
      this.error = null;
      this.currentParams = mergedParams;

      try {
        const payload = await userListCache.get(mergedParams, {
          ttlMs: USERS_LIST_TTL,
          force: options.force === true,
          loader: async () => {
            const response = await UserService.getList(mergedParams);
            return {
              items: Array.isArray(response.data?.data) ? response.data.data : [],
              meta: response.data?.meta || this.meta,
            };
          },
          onHydrate: ({ items, meta }) => {
            this.users = Array.isArray(items) ? items : [];
            this.meta = meta || this.meta;
            this.byId = this.users.reduce((acc, user) => {
              if (user?.id != null) acc[user.id] = user;
              return acc;
            }, {});
          },
        });

        return {
          data: {
            data: payload.items || [],
            meta: payload.meta || this.meta,
          },
        };
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch users";
        toast(this.error, "error");
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async ensureUsers(params = {}, options = {}) {
      return await this.fetchUsers(params, { ...options, force: false });
    },

    async createUser(userData) {
      this.loading = true;
      this.error = null;
      try {
        const response = await UserService.create(userData);
        this.invalidateUserCaches();
        toast("User created successfully!");
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to create user";
        toast(this.error, "error");
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updateUser(userData) {
      this.loading = true;
      this.error = null;
      try {
        const response = await UserService.update(userData.id, userData);
        this.invalidateUserCaches(userData.id);
        if (userData.fromUser !== null) toast("User updated successfully!");
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update user";
        toast(this.error, "error");
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchUserById(userId, options = {}) {
      this.loading = true;
      this.error = null;
      try {
        const payload = await userDetailCache.get(
          { id: Number(userId) || userId },
          {
            ttlMs: USERS_DETAIL_TTL,
            force: options.force === true,
            loader: async () => {
              const response = await UserService.getById(userId);
              return { user: response.data };
            },
            onHydrate: ({ user }) => {
              if (user?.id != null) this.byId = { ...this.byId, [user.id]: user };
            },
          }
        );
        return payload.user || null;
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch user";
        toast(this.error, "error");
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async ensureUserById(userId, options = {}) {
      return await this.fetchUserById(userId, { ...options, force: false });
    },

    async deleteUsers(userIds) {
      this.loading = true;
      this.error = null;
      try {
        await Promise.all(userIds.map((id) => UserService.delete(id)));
        this.invalidateUserCaches();
        toast("Users deleted successfully!");
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to delete users";
        toast(this.error, "error");
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async deleteUser(id) {
      this.loading = true;
      this.error = null;
      try {
        await UserService.delete(id);
        this.invalidateUserCaches(id);
        toast("User deleted successfully");
      } catch (err) {
        this.error = err.response?.data?.message || "Failed to delete user";
        toast(this.error, "error");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    invalidateUserCaches(userId = null) {
      userListCache.clear();
      if (userId != null) {
        userDetailCache.invalidate({ id: Number(userId) || userId });
      } else {
        userDetailCache.clear();
      }
    },
  },
});
